import {
  CardData,
  NFCTagInterfaceBase,
  NFCTagInterfaceManagerStatus,
  NFCTagInterfaceType,
} from '../NFCProxy';

interface NonPasswordResponse {
  success: boolean;
  error: string;
}
export class NFCTagHTTPProxyInterface implements NFCTagInterfaceBase {
  public name = 'Local HTTP NFC Proxy';
  public readonly type = NFCTagInterfaceType.HTTP;
  public supportsManualConnect = false;
  public status = NFCTagInterfaceManagerStatus.Ready;

  public httpAddress = '';
  public connected = false;
  public originalDeviceUID: string;

  public constructor(deviceUID: string) {
    this.originalDeviceUID = deviceUID;
  }

  public async init(): Promise<void> {
    // HTTP Proxy doesn't need initialization
  }

  public dispose(): void {
    // HTTP Proxy doesn't need disposing
  }

  public async checkCardBlank(uuid: string): Promise<{ success: boolean; status: string }> {
    const nonPasswordResponse = await fetch('http://127.0.0.1:7070/read', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        uuid,
      }),
    });

    const nonPasswordData = (await nonPasswordResponse.json()) as NonPasswordResponse;
    if (nonPasswordResponse.status !== 200) {
      return { success: false, status: `Failed to read tags: ${nonPasswordData.error}` };
    }

    if (!nonPasswordData.success) {
      if (nonPasswordData.error === 'Card is empty') {
        return { success: true, status: 'Card is blank' };
      }

      return { success: false, status: 'NFC badge already has a password' };
    }

    return {
      success: true,
      status: 'Card has no password, and is not blank (Can be reprogrammed)',
    };
  }

  public async writeCard(cardData: CardData): Promise<{ success: boolean; status: string }> {
    const writeResponse = await fetch('http://127.0.0.1:7070/write', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        attendeeId: cardData.attendeeId,
        conventionId: cardData.conventionId,
        issuance: cardData.issuance,
        timestamp: cardData.timestamp,
        password: cardData.password,
        uuid: cardData.uuid,
        signature: cardData.signature,
      }),
    });

    return (await writeResponse.json()) as { success: boolean; status: string };
  }

  public async setPassword(uuid: string, password: number): Promise<{ success: boolean }> {
    const response = await fetch('http://127.0.0.1:7070/setpassword', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        uuid,
        password,
      }),
    });

    return (await response.json()) as { success: boolean };
  }

  public async getUUID(): Promise<{ success: boolean; uuid: string }> {
    const response = await fetch('http://127.0.0.1:7070/uuid');
    return (await response.json()) as { success: boolean; uuid: string };
  }

  public async reset(): Promise<{ success: boolean }> {
    const response = await fetch('http://127.0.0.1:7070/reset');
    return (await response.json()) as { success: boolean };
  }

  public async readCard(
    uuid: string,
    password: number,
  ): Promise<{ success: boolean; card?: CardData }> {
    const response = await fetch('http://127.0.0.1:7070/read', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        uuid,
        password,
      }),
    });

    return (await response.json()) as { success: boolean; card?: CardData };
  }

  public startEventSource(
    onMessage: (event: string) => void,
    onError: (status: string) => void,
    onOpen: (status: string) => void,
  ): () => void {
    let eventSource: EventSource | null = null;
    let reconnectTimeout: number;

    const setupSSE = () => {
      if (eventSource) {
        eventSource.close();
      }

      eventSource = new EventSource('http://localhost:7070/events');

      eventSource.addEventListener('message', (event) => {
        try {
          const data = JSON.parse(event.data as string) as { Event: string };
          onMessage(data.Event);
        } catch {
          // Error parsing SSE event
        }
      });

      eventSource.addEventListener('error', () => {
        // SSE connection error
        eventSource?.close();
        this.connected = false;
        onError('NFC Proxy not running');
        // Schedule reconnection after 1 second
        reconnectTimeout = setTimeout(setupSSE, 1000);
      });

      eventSource.addEventListener('open', () => {
        this.connected = true;
        onOpen('NFC Proxy connected');
      });
    };

    // Initial connection
    setupSSE();

    // Return cleanup function
    return () => {
      if (eventSource) {
        eventSource.close();
      }

      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
    };
  }

  // httpAddress: string;
}
